/**
 * Admin-only runtime overrides for WHITELIST_PATTERNS.
 *
 * Stored in localStorage so changes persist across reloads on the admin's
 * browser only. Used by useSpamProtection to extend (or replace) the
 * built-in whitelist when developing/testing locally.
 *
 * Two lists are persisted:
 *  - extras:   admin-added regex source strings (case-insensitive)
 *  - disabled: indexes (as strings) of built-in patterns to skip
 */
const STORAGE_KEY = "hadley:spam-whitelist-overrides:v1";

export type WhitelistOverrides = {
  extras: string[];        // regex source strings (no slashes, no flags)
  disabled: number[];      // indexes into the built-in WHITELIST_PATTERNS array
};

const empty: WhitelistOverrides = { extras: [], disabled: [] };

export function readOverrides(): WhitelistOverrides {
  try {
    if (typeof window === "undefined") return empty;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<WhitelistOverrides>;
    return {
      extras: Array.isArray(parsed.extras) ? parsed.extras.filter((s) => typeof s === "string") : [],
      disabled: Array.isArray(parsed.disabled) ? parsed.disabled.filter((n) => Number.isInteger(n)) : [],
    };
  } catch {
    return empty;
  }
}

export function writeOverrides(next: WhitelistOverrides): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* storage disabled — silently ignore */
  }
}

export function clearOverrides(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Compile admin-added extras into RegExp. Invalid sources are dropped. */
export function compileExtras(extras: string[]): RegExp[] {
  const out: RegExp[] = [];
  for (const src of extras) {
    try {
      out.push(new RegExp(src, "i"));
    } catch {
      /* skip invalid pattern */
    }
  }
  return out;
}
